import Vue from 'vue';

var focusableElements = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]';

const scope = 'vue-accessible-modal';
var Events = {
    Show: `${scope}:show`,
    Close: `${scope}:close`,
    Confirm: `${scope}:confirm`,
};

var script = Vue.extend({
    name: 'VueAccessibleModal',
    data() {
        return {
            open: false,
            options: null,
            component: null,
            componentOptions: null,
            lastFocusedElement: null,
            confirmMessage: undefined,
            resolveReject: null,
        };
    },
    computed: {
        transition() {
            const { options, $_accessibleModalOptions } = this;
            if (options && options.transition) {
                return options.transition;
            }
            return $_accessibleModalOptions.transition;
        },
        className() {
            const { options, componentOptions } = this;
            const arr = ['v-modal'];
            if (options && options.classes) {
                arr.push(options.classes);
            }
            if (componentOptions && componentOptions.classes) {
                arr.push(componentOptions.classes);
            }
            return arr;
        },
        attributes() {
            const { options, componentOptions } = this;
            const obj = {};
            if (options && typeof options.attributes === 'object') {
                Object.assign(obj, options.attributes);
            }
            if (componentOptions && typeof componentOptions.attributes === 'object') {
                Object.assign(obj, componentOptions.attributes);
            }
            return obj;
        },
        props() {
            const { options, confirmMessage, resolveReject } = this;
            const obj = {};
            if (options && typeof options.props === 'object')
                Object.assign(obj, options.props);
            if (resolveReject)
                Object.assign(obj, resolveReject);
            if (confirmMessage)
                Object.assign(obj, { message: confirmMessage });
            return obj;
        },
        listeners() {
            const { options } = this;
            if (options && typeof options.listeners === 'object')
                return options.listeners;
            return {};
        },
        label() {
            const { options, componentOptions } = this;
            if (options && options.label)
                return options.label;
            if (componentOptions && componentOptions.label)
                return componentOptions.label;
        },
    },
    created() {
        this.$root.$on(Events.Show, this.show);
        this.$root.$on(Events.Confirm, this.confirm);
        this.$root.$on(Events.Close, this.close);
    },
    methods: {
        boot(component, options) {
            this.component = component;
            this.options = options || null;
            const { activeElement } = document;
            if (activeElement) {
                this.lastFocusedElement = activeElement;
            }
            this.$nextTick(() => {
                this.open = true;
            });
        },
        show(component, options) {
            this.boot(component, options);
        },
        confirm(component, message, resolveReject, options) {
            this.confirmMessage = message;
            this.resolveReject = resolveReject;
            this.boot(component, options);
        },
        close() {
            const { lastFocusedElement, resolveReject, props } = this;
            if (lastFocusedElement) {
                lastFocusedElement.focus();
            }
            if (resolveReject) {
                resolveReject.reject();
            }
            this.open = false;
            this.componentOptions = null;
        },
        getFocusableElements() {
            const { modal } = this.$refs;
            const elements = modal.querySelectorAll(focusableElements);
            // return only visible elements (e.g. display !== none)
            return Array.from(elements).filter(element => element.offsetParent !== null);
        },
        focusHandler(e) {
            const { activeElement } = document;
            const focusableElements = this.getFocusableElements();
            if (!focusableElements.length)
                return;
            const firstFocusableElement = focusableElements[0];
            const lastFocusedElement = focusableElements[focusableElements.length - 1];
            let element;
            if (activeElement === firstFocusableElement && e.shiftKey) {
                element = lastFocusedElement;
            }
            else if (activeElement === lastFocusedElement && !e.shiftKey) {
                element = firstFocusableElement;
            }
            if (element) {
                e.preventDefault();
                element.focus();
            }
        },
        mountedHook() {
            const focusableElements = this.getFocusableElements();
            if (focusableElements.length !== 0) {
                focusableElements[0].focus();
            }
            const { modal } = this.$refs.component.$options;
            if (modal !== undefined) {
                this.componentOptions = modal;
            }
        },
    },
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier
/* server only */
, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
  if (typeof shadowMode !== 'boolean') {
    createInjectorSSR = createInjector;
    createInjector = shadowMode;
    shadowMode = false;
  } // Vue.extend constructor export interop.


  var options = typeof script === 'function' ? script.options : script; // render functions

  if (template && template.render) {
    options.render = template.render;
    options.staticRenderFns = template.staticRenderFns;
    options._compiled = true; // functional template

    if (isFunctionalTemplate) {
      options.functional = true;
    }
  } // scopedId


  if (scopeId) {
    options._scopeId = scopeId;
  }

  var hook;

  if (moduleIdentifier) {
    // server build
    hook = function hook(context) {
      // 2.3 injection
      context = context || // cached call
      this.$vnode && this.$vnode.ssrContext || // stateful
      this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext; // functional
      // 2.2 with runInNewContext: true

      if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
        context = __VUE_SSR_CONTEXT__;
      } // inject component styles


      if (style) {
        style.call(this, createInjectorSSR(context));
      } // register component module identifier for async chunk inference


      if (context && context._registeredComponents) {
        context._registeredComponents.add(moduleIdentifier);
      }
    }; // used by ssr in case component is cached and beforeCreate
    // never gets called


    options._ssrRegister = hook;
  } else if (style) {
    hook = shadowMode ? function () {
      style.call(this, createInjectorShadow(this.$root.$options.shadowRoot));
    } : function (context) {
      style.call(this, createInjector(context));
    };
  }

  if (hook) {
    if (options.functional) {
      // register for functional component in vue file
      var originalRender = options.render;

      options.render = function renderWithStyleInjection(h, context) {
        hook.call(context);
        return originalRender(h, context);
      };
    } else {
      // inject component registration as beforeCreate hook
      var existing = options.beforeCreate;
      options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
    }
  }

  return script;
}

var normalizeComponent_1 = normalizeComponent;

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function() {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(
    "transition",
    {
      attrs: { name: _vm.transition },
      on: {
        "after-enter": function($event) {
          return _vm.$emit("show")
        },
        "after-leave": function($event) {
          return _vm.$emit("close")
        }
      }
    },
    [
      _vm.open
        ? _c(
            "div",
            _vm._b(
              {
                ref: "modal",
                class: _vm.className,
                attrs: {
                  "aria-label": _vm.label,
                  role: "dialog",
                  "aria-modal": "true"
                },
                on: {
                  keyup: function($event) {
                    if (
                      !$event.type.indexOf("key") &&
                      _vm._k($event.keyCode, "esc", 27, $event.key, [
                        "Esc",
                        "Escape"
                      ])
                    ) {
                      return null
                    }
                    return _vm.close($event)
                  },
                  keydown: function($event) {
                    if (
                      !$event.type.indexOf("key") &&
                      _vm._k($event.keyCode, "tab", 9, $event.key, "Tab")
                    ) {
                      return null
                    }
                    return _vm.focusHandler($event)
                  }
                }
              },
              "div",
              _vm.attributes,
              false
            ),
            [
              _c(
                "div",
                { staticClass: "v-modal__backdrop", on: { click: _vm.close } },
                [_vm._t("backdrop")],
                2
              ),
              _vm._v(" "),
              _c("div", { staticClass: "v-modal__inner" }, [
                _c("div", { staticClass: "v-modal__holder" }, [
                  _c(
                    "div",
                    { ref: "content", staticClass: "v-modal__content" },
                    [
                      _c(
                        _vm.component,
                        _vm._g(
                          _vm._b(
                            {
                              ref: "component",
                              tag: "component",
                              on: { "hook:mounted": _vm.mountedHook }
                            },
                            "component",
                            _vm.props,
                            false
                          ),
                          _vm.listeners
                        )
                      )
                    ],
                    1
                  )
                ])
              ])
            ]
          )
        : _vm._e()
    ]
  )
};
var __vue_staticRenderFns__ = [];
__vue_render__._withStripped = true;

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  

  
  var VueAccessibleModalComponent = normalizeComponent_1(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    undefined,
    undefined
  );

class VueAccessibleModal {
    constructor(vm) {
        this._root = vm.$root;
    }
    show(modal, options) {
        this._root.$emit(Events.Show, modal, options);
    }
    close() {
        this._root.$emit(Events.Close);
    }
    confirm(component, message, options) {
        return new Promise((resolve, reject) => {
            this._root.$emit(Events.Confirm, component, message, {
                resolve,
                reject,
            }, options);
        });
    }
}
const Plugin = {
    install(Vue, options = {}) {
        if (Vue.$_accessible_modal_installed)
            return;
        Vue.$_accessible_modal_installed = true;
        Vue.mixin({
            beforeCreate() {
                this.$modal = new VueAccessibleModal(this);
            },
        });
        Vue.component('VueAccessibleModal', VueAccessibleModalComponent);
        Vue.prototype.$_accessibleModalOptions = {
            transition:  options.transition,
        };
    },
};

export default Plugin;
